import styled from 'styled-components';

import { Container } from '../components/container/container';
import { Heading } from '../components/heading/heading';
import { Content, Copy } from '../components/register-screens/shared';

// #region Styles

const H4 = styled.h4({
  marginBottom: 16,
});

const Table = styled.div`
  table {
    border-collapse: collapse;
    margin: 32px 0;
    text-align: left;
    width: 100%;
  }
  table tr {
    border-bottom: 1px solid;
  }
  table th,
  table td {
    padding: 16px 0;
    font-size: 16px;
  }
  table td span {
    background: #eee;
    display: none;
    font-size: 10px;
    font-weight: 500;
    padding: 5px;
    position: absolute;
    text-transform: uppercase;
    top: 0;
    left: 0;
  }

  @media (max-width: 800px) {
    table thead {
      left: -9999px;
      position: absolute;
      visibility: hidden;
    }
    table tr {
      border-bottom: 0;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      margin-bottom: 8px;
    }
    table > tr:first-child {
      display: none;
    }
    table tr td:first-child {
      margin-right: 4px;
    }
    table tr td:last-child {
      margin-left: 4px;
    }
    table td {
      margin: 0 -1px -1px 0;
      padding-top: 35px;
      position: relative;
      width: calc(50% - 4px);
    }
    table td span {
      display: block;
    }
  }
`;

const Ul = styled.ul`
  list-style-type: disc;
`;

// #endregion

export function PrivacyPolicy() {
  return (
    <Container>
      <Content>
        <Heading center>Privacy policy</Heading>

        <Copy>
          <p>Effective Date: 10 June 2024</p>
          <H4>SCOPE</H4>
          <p>
            Welcome to the Sotheby&rsquo;s group of companies. This is the
            Privacy Policy of Sotheby&rsquo;s Inc, together with the wider
            Sothebys group of companies, details of which can be found{' '}
            <a
              href="https://www.sothebys.com/en/sothebys-group-of-companies"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>{' '}
            its beginnings in 1744, Sotheby&rsquo;s has built its business on
            the trust of its customers, clients and visitors. We endeavor to
            understand their interests in art and design and, at the same time,
            protect their privacy. Those values remain essential to us even as
            we have grown into a global network of offices in approximately 40
            countries. Sotheby&rsquo;s publishes this Privacy Policy to let you
            know how we collect, use, and disclose your personal information in
            the course of operating our business. It also describes your rights
            in relation to this information. In some circumstances, such as for
            certain events, we may provide additional information regarding our
            data practices. This Privacy Policy applies to any Sotheby&rsquo;s
            service, event, website, mobile application or other digital or
            non-digital service, and any hard copy (paper) form which provides a
            link to or refers to this Privacy Policy.
          </p>
          <p>
            If you register with or transact on any of our apps or other group
            websites the relevant privacy policy will be linked to on such
            sites.
          </p>
          <p>
            For those who reside in the United Kingdom, Switzerland or the
            European Economic Area, please see the end of this Policy for
            additional information regarding our practices. For California and
            Virginia residents, please see the section titled &ldquo;Additional
            Information for California and Virginia Residents&rdquo;.
          </p>
          <p>
            If you reside in mainland China or transact or consign there or use
            any of our Chinese apps, website or digital properties, please see
            the section below titled &ldquo;Additional Information for China
            Customers&rdquo; for additional information that applies to you.
          </p>
          <H4>INFORMATION SOTHEBY&rsquo;S COLLECTS ABOUT YOU</H4>
          <p>
            Personal information is information, or a combination of pieces of
            information, that could reasonably allow you to be identified. As
            described in more detail below, we collect personal information
            about you from a variety of sources, including information we
            collect from you directly, information we collect about you from
            other sources, and information we collect automatically from you.
          </p>
          <H4>Information We Collect Directly From You</H4>
          <p>
            We collect information that you submit to us face to face, over the
            telephone, on a paper form, by email or online or provide to us on
            forms available on third party websites. The types of information
            that we collect directly from you include: personal details (e.g.,
            name, date of birth, alias and/or avatar alias), contact details
            (e.g., phone number, email address, postal address), transaction
            information (e.g., bidding or purchase records, shipping details,
            information about items you purchase or wish to sell or consign),
            details about your collection, financial information (e.g.,
            tokenized payment information in connection with your purchases,
            wire instructions, bank account or bank transfer details, digital
            wallet details and address), username and password, user ID, unique
            device ID, information on forms required for certain transactions
            (e.g social security number), insurance details, and identification
            information (e.g. photo ID and proof of address).
          </p>
          <p>
            We rely on the information you provide to us or that we collect or
            observe about your individual interactions with us, for example, if
            you attend a live event, sell or consign property, participate in
            one of our auctions or sales, become a client, register or bid
            online.
          </p>
          <H4>Information We Collect From Other Sources</H4>
          <p>
            We may collect information about you from external sources,
            including social media sites and data supplementation services. The
            types of information we collect about you from other sources include
            your public profile information, family relationships,
            organizational affiliations, results of verification checks, nature
            of your connection to an organisation, your source of wealth,
            whether you are subject to sanctions, a PEP or a family member or
            close associate of a PEP. We may work to expand our customer base by
            acquiring names, contact data, financial information, affiliations,
            and demographic information from other sources such as private
            companies, public registers, and social media sites, where permitted
            by law. We may also generate information such as appraisals,
            profiles, and a history of our relationships with you based on the
            information you have provided or that we have obtained from other
            sources, as permitted under applicable law.
          </p>
          <H4>Information We Collect Automatically</H4>
          <p>
            We may make video recordings of our auctions (including for security
            reasons and to monitor bidding), gallery spaces, and certain live
            events.
          </p>
          <p>
            Live streaming of our auctions and certain events is available on
            our websites and on social media for certain auctions. Cameras and
            microphones recording images and sounds for live streaming focus
            primarily on auctioneers, presenters and our specialists but it is
            possible that background sound and images may be captured.
          </p>
          <p>
            We use CCTV at our sales rooms and offices for the protection of our
            staff, our visitors and the property we sell.
          </p>
          <p>
            We record calls made to our customer care and telephone bidding
            lines for quality assurance, compliance purposes, and recordkeeping
            purposes.
          </p>
          <p>
            We may use common data collection technologies as you visit our
            websites or apps or interact with our emails. For example:
          </p>
          <p>
            Our logs gather date, time, information about your browser and
            system or device configuration, information about how you interact
            with our digital properties, and an IP address for all visitors to
            our sites.
          </p>
          <p>
            If you have registered with us online, we use data collection
            technologies to collect information that indicates your individual
            interests in our websites, online platforms and apps, and your
            response to our emails and marketing campaigns.
          </p>
          <p>
            The Cookies and similar technologies on our digital platforms can
            also read and collect data about your browsers as you visit and
            interact with those platforms: please read our{' '}
            <a
              href="https://www.sothebys.com/en/cookie-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cookie Policy
            </a>{' '}
            for more detailed information about what cookies are, what kinds are
            used on our sites, and what choices you have. As described further
            in our{' '}
            <a
              href="https://www.sothebys.com/en/cookie-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cookie Policy
            </a>
            , we use these technologies to enable you to use our digital
            platforms, learn how you and others interact with our digital
            platforms, personalize the content on our digital platforms, and to
            deliver advertising based on your interests, including those
            inferred from your activities over time and across digital platforms
            operated by us and others.
          </p>
          <p>
            In general, our app will communicate data you provide about
            yourself, for example, registration, purchase, or bidding data, to
            our databases and systems. An app may, however, rely on other data
            collection technologies to recognize the device you use for viewing
            and to personalize your experience. If our app relies on additional
            data collection technologies that collect or use data about
            individual users, we will include additional notice either within
            the app or in a policy that accompanies it.
          </p>
          <p>
            We also use and allow certain other companies to use technologies
            that are similar to cookies (for example pixels and gifs) when we
            send you emails.
          </p>
          <H4>HOW SOTHEBY&rsquo;S USES INFORMATION ABOUT YOU</H4>
          <p>
            Sotheby&rsquo;s uses data about you for the following purposes, as
            permitted by applicable law:
          </p>
          <p>
            To provide you with the requested services. For example, bidding,
            selling, valuation, storage, shipping, loans and loan referral,
            restoration and expert advice.
          </p>
          <p>To manage and assure the integrity of our auctions.</p>
          <p>
            To on-board you as a client, verify your identity and sources of
            wealth and to satisfy our know your client obligations, to enter
            into and fulfill contracts with you, to fulfill your orders and
            purchases, facilitate sales or consignments, provide the
            publications, catalogs, and information you request, to manage your
            account, enquiries and requests and your relationship with us.
          </p>
          <p>
            To identify products, services, and offers you may be interested in
            based on your transactions and interactions with us, including but
            not limited to consignments, bidding histories, payment histories
            and events attended. &nbsp;
          </p>
          <p>
            To where you consign or sell property with us, advertise and sell
            your property on any of our international and country specific apps,
            websites and digital properties.
          </p>
          <p>
            To send you information about upcoming auctions, events, types of
            property and content that you may be interested in.
          </p>
          <p>
            To improve and personalize based on your inferred interests from our
            website and services.
          </p>
          <p>
            To match online ads to your interests, arrange for Sotheby&rsquo;s
            and other companies&rsquo; ads to reach you after you have left our
            sites, and help advertisers show you ads that are more relevant to
            your interests.
          </p>
          <p>To expand our online audiences.</p>
          <p>To provide, maintain, and protect our digital offerings.</p>
          <p>
            To monitor the performance of our websites, apps and digital
            properties and improve your user experience.
          </p>
          <p>
            To satisfy legal obligations and compliance requirements under laws
            and regulations applicable to Sotheby&rsquo;s Inc, and members of
            the wider Sothebys group of companies (both within your jurisdiction
            and internationally). For example, know your client, to check and
            verify your identity and to protect against the risk of fraud and to
            detect and prevent money laundering or other financial crime.
          </p>
          <p>
            To carry out anti money laundering checks, risk screening, crypto
            wallet and transaction screening and monitoring and blockchain
            analytics.&nbsp;
          </p>
          <p>
            To create and maintain records of art objects to assist us (and in
            the case of our public records including our online lot pages and
            online and printed catalogues to assist the public) with checks on
            authenticity, provenance, title and object value and to prevent
            fraud, theft or other unlawful activities.
          </p>
          <p>
            To provide you (where required) with a digital wallet to enable you
            to receive an NFT that you have purchased.
          </p>
          <p>
            To identify an alternative third party auction or sale house where
            your property does not meet our requirements or is not suitable for
            consignment with or sale by us. In the event you choose to consign
            or sell your property with such third party auction or sale house
            such third party will provide us with details of any sale to enable
            us to confirm and audit any introductory commission that may be due
            to us.
          </p>
          <p>
            To protect and defend our rights and property, you, or third
            parties.
          </p>
          <p>To keep you, our staff and property on our premises secure.</p>
          <p>
            To comply with legal obligations and regulations to which
            Sotheby&rsquo;s Inc and/or the wider Sothebys group of companies are
            subject and cooperate with regulators, other appropriate agencies or
            authorities and law enforcement bodies, both within your
            jurisdiction and internationally.
          </p>
          <p>
            To develop, improve and test certain of our products and services.
          </p>
          <p>
            To monitor the use of our services, train our staff and improve your
            client or your user experience. For example, through survey or
            market research.
          </p>
          <p>
            For other purposes that we tell you about specifically when you
            register or provide data about yourself to us.
          </p>
          <H4>HOW SOTHEBY&rsquo;S DISCLOSES INFORMATION ABOUT YOU</H4>
          <p>
            Your personal information will be processed by the Sotheby&rsquo;s
            entity that initially receives it. We also share information within
            the Sotheby&rsquo;s group of companies for the purposes set out in
            this privacy policy. Details of these entities may be found{' '}
            <a
              href="https://www.sothebys.com/en/sothebys-group-of-companies"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>
            .
          </p>
          <p>
            Sotheby&rsquo;s may disclose personal information to other companies
            or entities as follows:
          </p>
          <p>
            To business partners and vendors that work on our behalf to provide
            services such as property shipments, mailings, customer account and
            technology support, secure payment processing, experts who help us
            authenticate or value your property, fraud prevention, digital
            marketing management, and data and property storage or warehousing.
            One such entity is Stripe who is a payment processor. For details of
            how Stripe uses your personal information please see Stripe&rsquo;s
            privacy notice at{' '}
            <a
              href="https://stripe.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://stripe.com/privacy
            </a>
            .
          </p>
          <p>
            To our auction or sales partners in association with auctions or
            sales. For example, when we offer auctions or sales with our group
            or third-party partners, we may share auction or sale related data
            with those partners subject to the privacy policy or policies made
            available to you when registering for or bidding in association with
            the auctions or sales. &nbsp;
          </p>
          <p>
            To consigners, sellers, buyers, customs authorities and others as
            needed to facilitate a consignment, sale or purchase, including to
            arrange the collection, delivery or shipment of property.
          </p>
          <p>
            To organizations we partner with to host events and those who are
            involved in such events such as caterers and the event venues.
          </p>
          <p>
            To digital wallet and cryptocurrency providers as required to
            complete your sale or purchase. Please see their privacy policies
            for details of how they use your personal information. One such
            provider is Circle Internet Financial, LLC (&quot;Circle&quot;) that
            provides payment processing services on Sotheby&apos;s
            Metaverse.&nbsp; Note that as part of the process of minting NFTs,
            your digital wallet details will be made available on the public
            blockchain to enable the NFT to be made available to you and will be
            a permanent record of the transaction, along with the details of the
            NFT&rsquo;s you purchase. &nbsp;
          </p>
          <p>
            To law enforcement or other entities that present valid legal
            process or in our discretion, unless otherwise prohibited by law, to
            protect human safety, our rights, or the rights of others.
          </p>
          <p>
            To meet certain legal compliance requirements, for example under
            anti-money laundering laws or customs laws and regulations.&nbsp;
          </p>
          <p>
            To&nbsp;US, UK, EU, Asian and other domestic and overseas
            regulators, law enforcement agencies and authorities and other
            appropriate agencies or authorities in connection with their duties,
            such as preventing, investigating or detecting crime or to conduct
            money and trade sanction checks and to meet our compliance, legal
            and health and safety obligations.
          </p>
          <p>
            To identity checking and verification, AML, fraud and risk screening
            services, including for sanctions and/or negative media purposes.
            One such provider is Refinitiv Limited who check your data against
            various public data sets. Please see their World-Check privacy
            policy{' '}
            <a
              href="https://www.refinitiv.com/en/products/world-check-kyc-screening/privacy-statement"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>{' '}
            for details of how Refinitiv uses your personal information. Some of
            these screening services, such as Chainalysis Inc, may aggregate
            your data (such as cryptocurrency hashes and output addresses which
            are available publicly and user IDs) with their datasets to enable
            illicit activity to be identified. Further details can be obtained
            from Chainalysis&rsquo; website{' '}
            <a
              href="https://www.chainalysis.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>
          </p>
          <p>
            To an alternative third party auction or sale house where your
            property does not meet our requirements or is not suitable for sale
            by us to identify if you would like to consign or sell certain items
            with such third party. &nbsp;
          </p>
          <p>
            As part of a sale, merger, liquidation, or transfer of our business
            assets.
          </p>
          <p>
            We may share information with digital advertising and analytics
            firms so that they may recognize your devices and deliver
            interest-based content and advertisements to you. The information we
            share may include your name, postal address, email, device ID, or
            other identifier. These firms may collect additional information
            from you, such as your IP address and information about your browser
            or operating system; may combine information about you with
            information from other companies in data sharing cooperatives in
            which we participate; and may place or recognize their own unique
            cookie on your browser. To learn about certain choices you may have
            with regard to such sharing, please see our{' '}
            <a
              href="https://www.sothebys.com/en/cookie-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cookie Policy
            </a>{' '}
            or{' '}
            <a
              href="https://metaverse.sothebys.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              click here
            </a>
            .
          </p>
          <p>
            We may disclose information about you to Sotheby&rsquo;s
            International Realty, a company outside of the Sotheby&rsquo;s
            corporate group and a business partner that we coordinate our
            activities with from time to time
          </p>
          <H4>YOUR CHOICES</H4>
          <p>You have a choice about and control over:</p>
          <p>
            <strong>Receiving marketing messages from us.</strong> We may
            contact you by email, text, or SMS messaging. You may stop email
            marketing by using the &ldquo;opt out,&rdquo; or
            &ldquo;unsubscribe&rdquo; mechanism at the bottom of our email
            marketing messages. In most cases, we will give you a choice about
            stopping just one kind of email or opting out of all email marketing
            from us.
          </p>
          <p>
            <strong>Whether cookies can be set or read on your browser.</strong>{' '}
            You can learn more about controlling cookies placed on your device
            as you visit our sites in our{' '}
            <a
              href="https://www.sothebys.com/en/cookie-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cookie Policy
            </a>
            .
          </p>
          <p>
            <strong>Whether your account is up to date.</strong> You may review
            and edit the personal information that is stored in your user
            account on our website (e.g., your passwords and other contact
            information) by visiting the &ldquo;Profile&rdquo; area of your
            account on our website or by contacting Sotheby&rsquo;s via the
            email address at the end of this Policy. We will endeavor to respond
            to your request as soon as practicable. Before we are able to
            provide you with any information, correct any inaccuracies, or
            delete any information, however, we may ask you to verify your
            identity and to provide other details to help us to respond to your
            request.
          </p>
          <p>
            <strong>
              Your public profile on Sotheby&rsquo;s Metaverse and Metaverse
              notifications.&nbsp;
            </strong>
            Details of your bidding activity, your collection (i.e. any lots you
            have won) and your saved items (i.e. saved lots) will be displayed
            in your public profile on Sotheby&rsquo;s Metaverse if you are a
            Metaverse user. These details will be publicly available unless you
            change your settings. You will also receive notifications regarding
            saved items and items you have bid on. You can go to your Settings
            on Metaverse to change your settings in respect of the information
            that will be displayed as part of your public profile and the
            notifications you receive in relation to the Metaverse at any time.
          </p>
          <H4>INFORMATION SECURITY AND STORAGE</H4>
          <p>
            Sotheby&rsquo;s is a global company. We receive data collected
            locally by members of the Sotheby&rsquo;s group and collect data
            online directly from individuals in countries around the world.
            Sotheby&rsquo;s may in the normal course of its business transfer
            your personal information internationally to Sotheby&rsquo;s
            salesrooms, offices and representatives, and to other organisations
            who need to process your data in connection with the services that
            Sotheby&rsquo;s provides. We may process that data on servers
            globally. Your data will also be held on our servers in the USA. We
            have put recognized protections in place for the transfer of data
            from members of the Sotheby&rsquo;s group in the UK and EEA to
            regulate the processing of personal data outside of these countries.
            &nbsp;
          </p>
          <p>
            We protect your information using physical, technical, and
            administrative security measures to reduce the risks of loss,
            misuse, unauthorized access, disclosure, and alteration. Please be
            aware, though, that no security measures are perfect or
            impenetrable. You remain responsible for protecting your username
            and password and for the security of information you transmit to us
            over the Internet.
          </p>
          <p>
            We will keep your personal information for as long as we have a
            relationship with you. Once our relationship with you has come to an
            end, we will retain your personal information for a period of time
            that enables us to:
          </p>
          <Ul>
            <li>
              Maintain business records for analysis and/or audit purposes.
            </li>
            <li>
              Comply with record retention requirements under the law or other
              relevant legal or regulatory requirements.
            </li>
            <li>Defend or bring any existing or potential legal claims.</li>
            <li>Deal with any complaints regarding the services.</li>
            <li>Preserve historical records of transactions and property.</li>
          </Ul>
          <p>
            We will delete your personal information when it is no longer
            required for these purposes.
          </p>
          <H4>CHILDREN&rsquo;S DATA</H4>
          <p>
            Our websites are directed to adults. We do not accept children as
            clients or knowingly collect data about them.
          </p>
          <H4>THIRD PARTY WEBSITES</H4>
          <p>
            Our websites may contain links to other websites not owned or
            controlled by Sotheby&rsquo;s. Those websites may collect
            information about you. Sotheby&rsquo;s is not responsible for their
            practices or content.
          </p>
          <p>
            Additionally, we may work with auction partners so that you can bid
            on Sotheby&rsquo;s items from other sites or platforms. If you
            participate in an online auction on another company&rsquo;s site or
            platform, please note that those companies may transfer data about
            you to us to fulfill successful bids and that we may use that data
            in a manner consistent with this Policy. Please note that if you use
            another company&rsquo;s auction site, you should review the posted
            privacy policy applicable to that site.
          </p>
          <H4>Your Rights</H4>
          <p>
            You may ask us if you have questions about the data we hold on you.
            Under certain circumstances, subject to local law requirements, you
            may have rights under data protection laws applicable to your
            personal data. These may include a right to a copy of your personal
            information, to correct it, erase it, restrict our use of it, or to
            transfer it to other organizations at your request subject to local
            law. You may also have rights to object to some processing and,
            where we have asked for your consent to process your data, to
            withdraw this consent. Please contact us at{' '}
            <a
              href="mailto:privacycompliance@sothebys.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              privacycompliance@sothebys.com
            </a>{' '}
            if you wish to exercise any of the rights that apply to you and your
            personal data.&nbsp;
          </p>
          <p>For residents of:</p>
          <Ul>
            <li>
              California or Virginia please see the section below titled
              &ldquo;Additional Information for California and Virginia
              Residents&rdquo;
            </li>
            <li>
              EU, UK and Switzerland please see the section below titled
              &ldquo;Additional Information for EEA, UK and Switzerland
              Customers&rdquo;; and&nbsp;
            </li>
            <li>
              China please see the section below titled &ldquo;Additional
              Information for China Customers&rdquo;,
            </li>
          </Ul>
          <p>for more information about your privacy rights.&nbsp;</p>
          <H4>HOW TO CONTACT US</H4>
          <p>
            For questions about this Privacy Policy please email us at{' '}
            <a
              href="mailto:privacycompliance@sothebys.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              privacycompliance@sothebys.com
            </a>{' '}
            or contact:
          </p>
          <p>
            Sotheby&rsquo;s
            <br />
            Attn: Legal and Compliance Department
            <br />
            1334 York Avenue
            <br />
            New York, NY 10021
          </p>
          <H4>ADDITIONAL INFORMATION FOR CALIFORNIA AND VIRGINIA RESIDENTS</H4>
          <p>
            We may collect the following categories of personal information from
            and about you:
          </p>
          <Ul>
            <li>
              <strong>Identifiers:</strong> Such as your name, email address,
              phone number, IP address, device identifier, online identifier,
              client number and government-issued identification numbers you
              provide.
            </li>
            <li>
              <strong>Internet activity:</strong> Such as browsing history,
              search history, content or items viewed, account logins, and
              interactions with emails, digital ads, and other communications.
            </li>
            <li>
              <strong>Transactions:</strong> Such as bidding and purchase
              records, items considered for purchase or bidding, items offered
              for consignment, shipping details, and events attended.
            </li>
            <li>
              <strong>Payment information:</strong> Such as tokenized payment
              information or wire instructions.
            </li>
            <li>
              <strong>Demographics:&nbsp;</strong>Such as information about your
              gender or age.
            </li>
            <li>
              <strong>Location information:</strong> Such as the addresses you
              provide or your general location as inferred from your IP address
              or other network information.
            </li>
            <li>
              <strong>Professional and employment information:</strong> Such as
              information provided on valuation or credit documents.
            </li>
            <li>
              <strong>Video and audio:</strong> Such as recordings of auctions
              or client service calls.
            </li>
            <li>
              <strong>Inferences:</strong> Such as our assessment of the types
              of art, furniture, objects or jewelry in which you may have an
              interest.
            </li>
          </Ul>
          <p></p>
          <p>
            We collect information that may be considered sensitive under
            applicable laws, such as government identification numbers, login
            credentials for your Sotheby&rsquo;s account, precise geolocation,
            race or ethnic origin, religion, mental or physical health, sexual
            orientation, citizenship, or immigration status. We process such
            information only for those purposes expressly permitted under
            California law.
          </p>
          <p></p>
          <p>
            We collect and disclose all such information for the following
            business purposes:
          </p>
          <Ul>
            <li>
              To fulfill your orders and purchases, facilitate consignments,
              provide the services, publications, catalogs, and information you
              request, and manage your account, enquiries and requests and to
              manage your relationship with us.
            </li>
            <li>
              To send you information about upcoming events and content that you
              may be interested in. However, we do not use or disclose payment
              information for such purposes. &nbsp;
            </li>
            <li>
              To improve and personalize our website and services based on your
              inferred interests.&nbsp;
            </li>
            <li>
              To match online ads to your interests, arrange for Sotheby&apos;s
              and other companies&apos; ads to reach you after you have left our
              sites, and help advertisers show you ads that are more relevant to
              your interests. However, we do not use or disclose race, ethnic
              origin, religion, mental or physical health, sexual orientation,
              citizenship, or immigration status for such purposes.&nbsp;
            </li>
            <li>
              To obtain services such as item shipments, mailings, customer
              account and technology support, secure payment processing, fraud
              prevention, digital marketing management, and data storage.
            </li>
            <li> To expand our online audiences.</li>
            <li>To provide, maintain, and protect our digital offerings.</li>
            <li>
              To check and verify your identity and to protect against the risk
              of fraud and to detect money laundering or other financial crime.
            </li>
            <li>
              To carry out anti money laundering checks, risk screening, crypto
              wallet and transaction screening and monitoring and blockchain
              analytics.
            </li>
            <li>
              To provide you (where required) with a digital wallet to enable
              you to receive an NFT that you have purchased.
            </li>
            <li>
              To protect and defend our rights and property, you, or third
              parties.
            </li>
            <li>To coordinate auctions with business partners.</li>
            <li>To coordinate events.</li>
            <li>For legal and compliance purposes.</li>
            <li>
              To cooperate with law enforcement or other reasonable requests for
              information.
            </li>
            <li>
              To support potential sales, mergers, liquidations, or transfers of
              our business assets.
            </li>
            <li>
              For other purposes that we inform you about specifically when you
              register or provide data about yourself to us.
            </li>
          </Ul>
          <p></p>
          <p>
            We disclose all of the categories of personal information noted
            above with service providers that process personal information on
            our behalf (e.g., data storage providers, email services, Internet
            service providers, and research firms), business partners (e.g.,
            event coordinators, professional advisors and consultants),
            government and law enforcement entities, advertising and marketing
            companies, and our affiliates.
          </p>
          <p>
            We will keep your personal information for as long as we have a
            relationship with you. Once our relationship with you has come to an
            end, we will retain your personal information for a period of time
            that enables us to:
          </p>
          <p>Maintain business records for analysis and/or audit purposes.</p>
          <p>
            Comply with record retention requirements under the law or other
            relevant legal or regulatory requirements.
          </p>
          <p>Defend or bring any existing or potential legal claims.</p>
          <p>Deal with any complaints regarding the services.</p>
          <p>Preserve historical records of transactions and property.</p>
          <p>
            We will delete your personal information when it is no longer
            required for these purposes.
          </p>
          <p>
            We also disclose certain personal information in exchange for
            services, insights, or other valuable consideration. Such
            disclosures may be considered &ldquo;sales&rdquo; even if no money
            is exchanged. And we disclose certain personal information for
            purposes of targeting advertising on unaffiliated or distinctly
            branded websites or applications. Such disclosures are considered a
            &ldquo;share&rdquo; under California law. We may share or sell the
            following categories of information to the third parties listed
            below:
          </p>
          <p></p>
          <Table>
            <tbody>
              <tr>
                <td valign="middle">
                  <p>
                    <strong>
                      Categories of personal information disclosed in exchange
                      for valuable consideration or for targeted advertising
                    </strong>
                  </p>
                </td>
                <td valign="middle">
                  <p>
                    <strong>
                      Categories of third parties to whom this information is or
                      was disclosed in the past12 months
                    </strong>
                  </p>
                </td>
              </tr>
              <tr>
                <td valign="middle">
                  <p>Identifiers</p>
                </td>
                <td valign="middle">
                  <p>
                    Digital advertising and analytics companies, marketing
                    partners, social media platforms, auction partners and event
                    co-hosts.
                  </p>
                </td>
              </tr>
              <tr>
                <td valign="middle">
                  <p>Internet activity</p>
                </td>
                <td valign="middle">
                  <p>
                    Digital advertising and analytics companies, social media
                    platforms, and marketing partners.
                  </p>
                </td>
              </tr>
              <tr>
                <td valign="middle">
                  <p>Transactions</p>
                </td>
                <td valign="middle">
                  <p>
                    Digital advertising and analytics companies, auction
                    partners and event co-hosts, and marketing partners.
                  </p>
                </td>
              </tr>
              <tr>
                <td valign="middle">
                  <p>Payment information</p>
                </td>
                <td valign="middle">
                  <p>N/A</p>
                </td>
              </tr>
              <tr>
                <td valign="middle">
                  <p>Demographics</p>
                </td>
                <td valign="middle">
                  <p>
                    Digital advertising and analytics companies, marketing
                    partners, social media platforms, auction partners and event
                    co-hosts.
                  </p>
                </td>
              </tr>
              <tr>
                <td valign="middle">
                  <p>Location information</p>
                </td>
                <td valign="middle">
                  <p>
                    Digital advertising and analytics companies, auction
                    partners and event co-hosts.
                  </p>
                </td>
              </tr>
              <tr>
                <td valign="middle">
                  <p>Professional and employment information</p>
                </td>
                <td valign="middle">
                  <p>
                    Marketing partners, auction partners, and event co-hosts.
                  </p>
                </td>
              </tr>
              <tr>
                <td valign="middle">
                  <p>Video and audio information</p>
                </td>
                <td valign="middle">
                  <p>Auction partners and event co-hosts.</p>
                </td>
              </tr>
              <tr>
                <td valign="middle">
                  <p>Inferences</p>
                </td>
                <td valign="middle">
                  <p>
                    Digital advertising and analytics companies, marketing
                    partners, social media platforms, auction partners and event
                    co-hosts.
                  </p>
                </td>
              </tr>
            </tbody>
          </Table>
          <p>Residents of California and Virginia have the following rights:</p>
          <Ul>
            <li>
              <strong>Right to know.</strong> You may request that we provide
              you with information about the categories of your personal
              information that we collect, the specific pieces of personal
              information that we hold about you, the categories of sources from
              which we collected the information, our reasons for collecting and
              sharing the information, and the types of third parties with which
              we share the information.
            </li>
            <li>
              <strong>Right to deletion.</strong> You may ask us to delete your
              personal information, subject to certain exceptions.
            </li>
            <li>
              <strong>
                Right to opt out of sales or the processing of information for
                targeted advertising.
              </strong>{' '}
              You have the right to opt out of certain disclosures of your
              personal information for valuable consideration&nbsp;and to opt
              out of the processing of personal information for targeted
              advertising. You can exercise this right through the{' '}
              <a
                href="https://www.sothebys.com/do-not-sell-or-share-my-personal-information-for-targeted-advertising"
                target="_blank"
                rel="noopener noreferrer"
              >
                &ldquo;Do Not Sell or Share My Personal Information&rdquo;
              </a>{' '}
              link or by calling the toll free number 1-833-543-0067.
            </li>
            <li>
              <strong>Right to opt out of profiling.</strong> You may have the
              right to opt out of profiling that supports decisions that have
              legal or similarly significant effects. We do not engage in such
              profiling
            </li>
            <li>
              <strong>Right to be free from discrimination.</strong> You have
              the right to not be discriminated against for exercising any of
              the above-listed rights. We may, however, provide a different
              level of service or charge a different rate reasonably relating to
              the value of your personal information.
            </li>
            <li>
              <strong>Right to appeal</strong>. You may have the right to appeal
              a decision we make regarding your rights request.
            </li>
          </Ul>
          <p></p>
          <p>
            If you would like to exercise any of the above rights, please submit
            your request at{' '}
            <a
              href="mailto:privacycompliance@sothebys.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              privacycompliance@sothebys.com
            </a>
            .
          </p>
          <p>
            Please note that we may request specific information from you in
            order to verify your identity, and there may be circumstances where
            we will not be able to honor your request. For example, if you
            request deletion, we may need to retain certain personal information
            to comply with our legal obligations or other permitted purposes. We
            will only use personal information provided in a verifiable consumer
            request to verify your identity or authority to make the request. If
            you are submitting a request through an authorized agent, the
            authorized agent must provide us with your signed written permission
            stating that the agent is authorized to make the request on your
            behalf. We may also request that any authorized agents verify their
            identity and may reach out to you directly to confirm that you have
            provided the agent with your permission to submit the request on
            your behalf.
          </p>
          <H4>
            ADDITIONAL INFORMATION FOR EEA, UK, AND SWITZERLAND CUSTOMERS &amp;
            VISITORS TO SOTHEBY&rsquo;S SITES AND APPS
          </H4>
          <H4>Who is responsible for your data?</H4>
          <p>
            If you transact in an auction or private sale in a Sotheby&rsquo;s
            office in the EEA, UK, or Switzerland, then the Sotheby&rsquo;s
            entity running the auction or sale will be the data controller for
            that data. The name and contact details for this entity will be set
            out in your consignment agreement, private sale agreement, invoice,
            or{' '}
            <a
              href="https://metaverse.sothebys.com/conditions-of-business"
              target="_blank"
              rel="noopener noreferrer"
            >
              Conditions of Business
            </a>{' '}
            for the auction or sale.
          </p>
          <p>
            If you visit Sothebys.com, Sotheby&rsquo;s Metaverse or use the
            Sotheby&rsquo;s Inc app, then the data controller will be
            Sotheby&rsquo;s, a US entity, and this Policy contains our contact
            details. Sotheby&rsquo;s main establishment in the EU is
            Sotheby&rsquo;s France. Sotheby&rsquo;s in London is the
            Sotheby&rsquo;s UK-based establishment.
          </p>
          <H4>
            What is the legal basis on which Sotheby&rsquo;s relies to process
            your data?
          </H4>
          <p>
            On some occasions, Sotheby&rsquo;s processes your data with your
            consent (e.g., when you agree that we may place cookies, or if you
            ask Sotheby&rsquo;s to send you information about upcoming events).
          </p>
          <p>
            On other occasions, Sotheby&rsquo;s processes your data when we need
            to do this to fulfill a contract with you (e.g., for billing
            purposes) or where we are required to do this by law (e.g., where we
            have to fulfill anti-money laundering requirements). If it is
            mandatory for you to provide data for these purposes, we will make
            this clear at the time and will also explain what will happen if you
            do not provide the data (e.g., that we will not be able to process a
            bid at auction).
          </p>
          <p>
            Sotheby&rsquo;s also processes your data when it is our legitimate
            interests to do this and when these interests are not overridden by
            your data protection rights. For example, Sotheby&rsquo;s has a
            legitimate interest in ensuring the security and integrity of our
            auctions, in learning about the interests and preferences of our
            current and prospective clients, in developing new business
            opportunities, in maintaining accurate business and provenance
            records, and in ensuring that our websites and apps operate
            effectively. When we process personal information to meet our
            legitimate interests, we put in place robust safeguards to ensure
            that your privacy is protected and to ensure that our legitimate
            interests are not overridden by your interests or fundamental rights
            and freedoms.
          </p>
          <H4>Data transfers</H4>
          <p>
            Sotheby&rsquo;s may transfer personal information to countries
            outside the EEA, Switzerland and the UK, including to countries
            which have different data protection standards to those which apply
            in the EEA, Switzerland and the UK. Sotheby&rsquo;s has put in place
            European Commission approved standard contractual clauses to protect
            this data. For more information on the appropriate safeguards in
            place, please contact us at the details above.
          </p>
          <H4>Automatic Decision-Making</H4>
          <p>
            The way we analyse personal information for advertising and
            marketing purposes and for client development, risk assessment, or
            fraud prevention may involve profiling, which means that we may
            process your personal information using software that is able to
            evaluate your personal aspects and predict risks or outcomes. For
            example, we may use the information we collect (e.g., bidding and
            purchase information, browsing history, and consignment history) to
            infer your interests. And we may use those inferences to support
            automated decisions about the content, recommendations, and offers
            we present to you on our digital properties. We may use automated
            tools to flag for further review suspicious activities associated
            with our services (e.g., multiple logins from different locations
            within a short period of time or activities associated with
            suspicious IP addresses). These automated activities will not, in
            themselves, have legal or similar effects for you.
          </p>
          <H4>Your rights</H4>
          <p>
            You may ask Sotheby&rsquo;s for a copy of your personal information,
            to correct it, erase it, restrict our use of it, or to transfer it
            to other organizations at your request subject to local law. You
            also have rights to object to some processing and, where we have
            asked for your consent to process your data, to withdraw this
            consent. In particular, you have rights to object to direct
            marketing at any time.
          </p>
          <p>
            Where we process your data because we have a legitimate interest in
            doing so (as explained above), you also have a right to object to
            this. These rights may be limited in some situations for example,
            where we can demonstrate that we have a legal requirement to process
            your data.
          </p>
          <p>
            If you would like to discuss or exercise such rights, please contact
            us at the details below. We encourage you to contact us to update or
            correct your information if it changes or if the personal
            information we hold about you is inaccurate. We will contact you if
            we need additional information from you in order to honor your
            requests.
          </p>
          <p>
            We hope that we can satisfy queries you may have about the way we
            process your data. We are committed to working with you to obtain a
            fair resolution of any complaint or concern about privacy. However,
            if you have unresolved concerns and believe that we have not been
            able to assist with your complaint or concern, you also have the
            right to complain to data protection authorities.
          </p>
          <p>
            For questions about this Privacy Policy please email us at{' '}
            <a
              href="mailto:privacycompliance@sothebys.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              privacycompliance@sothebys.com
            </a>
            or contact:
          </p>
          <p>
            Sotheby&rsquo;s
            <br />
            Attn: Legal and Compliance Department
            <br />
            1334 York Avenue
            <br />
            New York, NY 10021
          </p>
          <p></p>
          <H4>ADDITIONAL INFORMATION FOR CHINA CUSTOMERS</H4>
          <p></p>
          <p>
            There may be some circumstances where we will seek further consents
            from you. For example, if we change the purposes or legal basis for
            processing your personal information, how we process your personal
            information, recipients of your personal information, the types of
            personal information we process or when we process sensitive
            personal information. Please indicate your consent when prompted.
            Please note that if you do not provide your consent, we may not be
            able to provide some or all of our services to you.
          </p>
          <p></p>
          <p>
            In circumstances where your personal information is shared between
            and used by members of the Sotheby&rsquo;s group which are based
            outside China, each such member will process your personal
            information in accordance with the Privacy Policy. You may contact
            them by using the contact details mentioned in &ldquo;How to Contact
            Us&rdquo;. Where we ask for your separate consent for certain
            sharing and transfers, please indicate your consent when
            prompted.&nbsp;Please note that if you do not provide your consent,
            we may not be able to provide some or all of our services to you.
          </p>
          <p>
            If you reside in mainland China or consign or transact there or use
            our Chinese apps or website your personal information may also be
            held on our servers in mainland China. Your account will be serviced
            by our international and Chinese based client teams.
          </p>
          <H4>Your Rights</H4>
          <p>
            You may ask Sotheby&rsquo;s for a copy of your personal information,
            to correct it, erase it, restrict our use of it, or to transfer it
            to other organizations at your request, subject to local law. You
            also have rights to object to some processing and, where we have
            asked for your consent to process your data, to withdraw this
            consent. In particular, you have rights to object to direct
            marketing at any time. Please note that if you withdraw your consent
            to processing your information, we may not be able to provide some
            or all of our services to you. We will advise you if a service is no
            longer available to you. You may also not be able to use all of the
            functionality that is available on our digital properties, websites
            or apps.
          </p>
          <p>
            If you would like to discuss or exercise such rights, please contact
            us at{' '}
            <a
              href="mailto:privacycompliance@sothebys.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              privacycompliance@sothebys.com
            </a>
            . We encourage you to contact us to update or correct your
            information if it changes or if the personal information we hold
            about you is inaccurate. We will contact you if we need additional
            information from you in order to honor your requests.
          </p>
          <p>
            To exercise your rights in respect of your personal information,
            please contact us through the details set out in the &ldquo;How to
            Contact Us&rdquo; section above.
          </p>
          <H4>CHANGES TO THIS POLICY</H4>
          <p>
            You may request a copy of this Policy from us using the contact
            details set out above. We may modify or update this Policy from time
            to time. If we change this Policy, we will notify you of the changes
            by updating this Policy on our website. Where changes to this Policy
            notice will have a fundamental impact on the nature of the
            processing or otherwise have a substantial impact on you, we will
            give you sufficient advance notice so that you have the opportunity
            to exercise your rights.
          </p>
        </Copy>
      </Content>
    </Container>
  );
}
